import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MMEC_COLOR_APPLICATIONS } from '../utils/mmecBrandColors';

interface SentimentBarProps {
  bullishValue: number;
  bearishValue: number;
  label?: string;
  showLegend?: boolean;
  height?: number;
  className?: string;
}

/**
 * SentimentBar - A horizontal stacked bar showing bullish vs bearish pressure
 * Based on volume data to indicate winning direction (buy vs sell)
 */
const SentimentBar: React.FC<SentimentBarProps> = ({
  bullishValue,
  bearishValue,
  label = 'Current price',
  showLegend = true,
  height = 40,
  className = ''
}) => {
  useTheme();
  const { language } = useLanguage();

  // Calculate percentages
  const total = Math.abs(bullishValue) + Math.abs(bearishValue);
  const bullishPercent = total > 0 ? (Math.abs(bullishValue) / total) * 100 : 0;
  const bearishPercent = total > 0 ? (Math.abs(bearishValue) / total) * 100 : 0;

  // Determine which direction is winning
  const isBullish = bullishValue > bearishValue;
  const winningDirection = isBullish ? 'bullish' : 'bearish';

  const translations = {
    bullish: language === 'ar' ? 'صاعد' : 'Bullish',
    bearish: language === 'ar' ? 'هابط' : 'Bearish',
    currentPrice: language === 'ar' ? 'السعر الحالي' : 'Current price'
  };

  return (
    <div
      className={`${className} p-3`}
      style={{ background: 'var(--elev)', border: '1px solid var(--hair)', borderRadius: 10, fontFamily: 'var(--font-ui)' }}
    >
      {/* Legend */}
      {showLegend && (
        <div className="flex justify-center gap-4 mb-2">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: MMEC_COLOR_APPLICATIONS.boughtPuts }}
            />
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{translations.bearish}</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: MMEC_COLOR_APPLICATIONS.boughtCalls }}
            />
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{translations.bullish}</span>
          </div>
        </div>
      )}

      {/* Bar Container */}
      <div className="flex items-center gap-3">
        {/* Label */}
        <div className="min-w-[100px]" style={{ fontSize: 13, fontWeight: 500, color: 'var(--txt)' }}>
          {label || translations.currentPrice}
        </div>

        {/* Bar */}
        <div className="flex-1 relative" style={{ height: `${height}px` }}>
          <div className="w-full h-full flex rounded overflow-hidden border" style={{ borderColor: 'var(--hair)' }}>
            {/* Bearish segment (red) */}
            {bearishPercent > 0 && (
              <div
                style={{
                  width: `${bearishPercent}%`,
                  backgroundColor: MMEC_COLOR_APPLICATIONS.boughtPuts,
                  transition: 'width 0.3s ease'
                }}
                className="h-full"
                title={`${translations.bearish}: ${bearishPercent.toFixed(1)}%`}
              />
            )}
            
            {/* Bullish segment (green) */}
            {bullishPercent > 0 && (
              <div
                style={{
                  width: `${bullishPercent}%`,
                  backgroundColor: MMEC_COLOR_APPLICATIONS.boughtCalls,
                  transition: 'width 0.3s ease'
                }}
                className="h-full"
                title={`${translations.bullish}: ${bullishPercent.toFixed(1)}%`}
              />
            )}
          </div>
        </div>

        {/* Percentage Display */}
        <div className="min-w-[80px] text-right" style={{ fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
          <div style={{ color: isBullish ? 'var(--up)' : 'var(--down)' }}>
            {winningDirection === 'bullish' ? bullishPercent.toFixed(1) : bearishPercent.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default SentimentBar;

